import { QUILL_MANAGER_TOKEN } from '@/container';
import useConversationForm from '@/hooks/conversations/use-conversation-form';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { QuillModule } from '@/services/quill';
import { useConfigurationStore } from '@/stores/configuration';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useDeviceStore } from '@/stores/device';
import { generateSessionKey } from '@/utils/session';
import 'quill/dist/quill.core.css';
import { useDependency } from 'ditox-react';
import { pick } from 'lodash-es';
import { useSubscription } from 'observable-hooks';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type QuillEditorProps = {
  onFocus?: () => void;
  initialValue?: string;
};

export interface EditorActions {
  focus: () => void;
  blur: () => void;
  insertText: (text: string, position?: number, focus?: boolean) => void;
  insertMention: (mentionId: number, focus?: boolean) => void;
  reset: (keepFocus?: boolean) => void;
  closeMentionMenu: () => void;
}

const QuillEditor = forwardRef<EditorActions, QuillEditorProps>(({ onFocus, initialValue }, ref) => {
  const { t } = useTranslation();
  const { type, id } = useConversationStore();
  const { mentionSources, submit, form } = useConversationForm();
  const { enterAsSubmit, alwaysEnterAsSubmit } = useDeviceStore();
  const quill = useDependency(QUILL_MANAGER_TOKEN);
  const isDesktop = useIsDesktop();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleUpdateDraft = () => {
    const key = generateSessionKey(type, id);
    const draft = pick(form.getValues(), ['Content', 'Quote', 'AtIDs']);
    draft.Content = draft.Content?.replace(/\n$/gi, '');

    if (draft.Content || draft.Quote) {
      useConversationSessionStore.getState().updateDraft(key, draft);
    } else {
      useConversationSessionStore.getState().deleteDraft(key);
    }
  };

  useEffect(() => {
    useConfigurationStore.persist.rehydrate();
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      quill.initialize(
        containerRef.current,
        t('conversation.placeholder'),
        type === 'group' ? [QuillModule.Mention] : [],
      );
    }

    return () => {
      quill.dispose();
    };
  }, [quill, t, type]);

  useEffect(() => {
    if (initialValue) {
      quill.initialContent = initialValue;
    }
  }, [initialValue, quill]);

  useEffect(() => {
    if (mentionSources.length > 0) {
      quill.mentionSources = mentionSources;
    }
  }, [mentionSources, quill]);

  useSubscription(quill.event$, (event) => {
    switch (event.action) {
      case 'focus':
        if (event.data) onFocus?.();
        break;
      case 'submit':
        if (form.formState.isSubmitting) return;
        submit();
        break;
      case 'content':
        if (event.data) {
          form.setValue('Content', event.data);
        } else {
          form.resetField('Content');
        }
        break;
      case 'draft':
        handleUpdateDraft();
        break;
      case 'attachment':
        form.setValue('AttachmentType', event.data.type);
        form.setValue('Attachment', [event.data.uploadable]);
        break;
    }
  });

  useEffect(() => {
    quill.enterAsSubmit = isDesktop || alwaysEnterAsSubmit || Boolean(enterAsSubmit);

    if (isDesktop || alwaysEnterAsSubmit) quill.focus();
  }, [enterAsSubmit, isDesktop, quill, alwaysEnterAsSubmit]);

  useImperativeHandle(
    ref,
    (): EditorActions => ({
      focus: quill.focus,
      blur: quill.blur,
      reset: quill.reset,
      insertText: quill.insertText,
      insertMention: quill.insertMention,
      closeMentionMenu: quill.dismissMentionMenu,
    }),
  );

  return (
    <div
      id='quill-editor'
      ref={containerRef}
      className='h-full max-h-36 flex-1 overflow-auto break-all !border-none !text-base !outline-none'
    />
  );
});

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;
